import {useState} from 'react';

import {sort} from 'ramda';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import {buildFeatureTreesFromCatalogueKeys} from '../../../../FeaturesCheckboxTree/utils/buildFeatureTreesFromCatalogueKeys';
import {collapseSelectedFeatureValues} from '../../../../FeaturesCheckboxTree/utils/collapseSelectedFeatureValues';
import {expandSelectedFeatureValues} from '../../../../FeaturesCheckboxTree/utils/expandSelectedFeatureValues';

const EMPTY_FEATURES: string[] = [];

type UseFeaturesFilterFieldStateParams = {
  features: string[];
  onFeaturesChange: (value: string[]) => void;
  isInteractionDisabled?: boolean;
};

export function useFeaturesFilterFieldState(params: UseFeaturesFilterFieldStateParams) {
  const {features, onFeaturesChange, isInteractionDisabled = false} = params;
  const {
    getFeatureLabel,
    hasFeatureTranslation,
    isLoading,
    features: catalogueFeatures,
  } = useCatalogue();
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<string[]>(EMPTY_FEATURES);

  const translatableFeatures =
    catalogueFeatures?.filter((feature) => hasFeatureTranslation(feature.const_key)) ?? [];

  const sortedFeatureItems = sort(
    (left, right) => getFeatureLabel(left).localeCompare(getFeatureLabel(right)),
    features
  ).map((featureKey) => ({key: featureKey, label: getFeatureLabel(featureKey)}));

  const openDialog = () => {
    if (isLoading || isInteractionDisabled) {
      return;
    }

    const featureTrees = buildFeatureTreesFromCatalogueKeys(translatableFeatures);
    setDraftValue(expandSelectedFeatureValues(features, featureTrees));
    setIsOpen(true);
  };

  const confirmSelection = (value: string[]) => {
    const featureTrees = buildFeatureTreesFromCatalogueKeys(translatableFeatures);
    onFeaturesChange(collapseSelectedFeatureValues(value, featureTrees));
    setIsOpen(false);
  };

  return {
    sortedFeatureItems,
    isOpen,
    draftValue,
    isLoading,
    isTriggerDisabled: isLoading || isInteractionDisabled,
    onDraftChange: setDraftValue,
    onOpen: openDialog,
    onClose: () => setIsOpen(false),
    onConfirm: confirmSelection,
    onChange: onFeaturesChange,
  };
}
